import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import { LANGUAGE_STORAGE_KEY } from "../utils/constants/constants";
import useOrgScopeStore from "../utils/stores/useOrgScopeStore";
import en from "./locale/en/translation.json";
import kz from "./locale/kz/translation.json";
import ru from "./locale/ru/translation.json";

// Supported UI languages. Note the deliberate mismatch: the Kazakh catalog is
// stored in the `kz/` FOLDER (mirrors psy_frontend) while the language CODE is
// `kk` — ISO 639-1, and what the backend's LANGUAGES and Accept-Language expect.
// Do not introduce a third spelling; `kz` is a country code, not a language.
export const SUPPORTED_LANGUAGES = ["kk", "ru", "en"];

/**
 * Repair a stored language tag before the detector reads it.
 *
 * Two ways the stored value goes bad, both silent:
 *   1. `kz` — plausible given the folder name, and it was reachable in the
 *      window between the i18n foundation landing and the UI being locked to
 *      English. It is not in `supportedLngs`, so i18next renders the fallback
 *      while `i18n.language` keeps reporting "kz"; worse,
 *      `Intl.DateTimeFormat.supportedLocalesOf(["kz"])` is `[]`, so every date
 *      silently formats in the browser default rather than throwing.
 *   2. any other unsupported tag, which should fall through to navigator
 *      detection rather than pin the UI to a language we cannot render.
 */
const normalizeStoredLanguage = () => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (!stored) return;
    const base = stored.split("-")[0].toLowerCase();
    const fixed = base === "kz" ? "kk" : base;
    if (!SUPPORTED_LANGUAGES.includes(fixed)) {
      localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    } else if (fixed !== stored) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, fixed);
    }
  } catch {
    // Storage disabled (private mode / blocked cookies). Detection falls
    // through to navigator, which is the correct degraded behaviour.
  }
};

normalizeStoredLanguage();

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // No `lng` here on purpose: setting it makes i18next skip the detector
    // entirely, which is precisely how the switcher was disabled before.
    fallbackLng: {
      // A missing Kazakh string is more useful to a Kazakhstani reader in
      // Russian than in English.
      kk: ["ru", "en"],
      ru: ["en"],
      // PRODUCT DECISION — Kazakh is THE default: with no saved choice the
      // detector finds nothing (navigator is deliberately not consulted, see
      // `detection` below) and i18next lands on the first entry here. The
      // rest of the chain covers missing strings for that undetected case.
      default: ["kk", "ru", "en"],
    },
    supportedLngs: SUPPORTED_LANGUAGES,
    load: "languageOnly", // map "ru-RU" -> "ru", "kk-KZ" -> "kk"
    detection: {
      // localStorage ONLY — no `navigator`. A first-time visitor gets the
      // state language regardless of their browser locale (most KZ browsers
      // report ru, which would silently make Russian the de-facto default);
      // an explicit pick in the switcher persists and wins from then on.
      order: ["localStorage"],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ["localStorage"],
    },
    interpolation: { escapeValue: false },
    resources: {
      en,
      kk: kz,
      ru,
    },
    ns: ["translations"],
    defaultNS: "translations",
    react: { useSuspense: false },
  });

// Keep <html lang> in step with the UI. It drives screen-reader pronunciation,
// browser translation prompts and CSS `:lang()` hyphenation — none of which the
// React tree can set for itself. This was never wired up originally.
const syncDocumentLanguage = (lng) => {
  document.documentElement.lang = lng || "en";
};

syncDocumentLanguage(i18n.resolvedLanguage);
i18n.on("languageChanged", syncDocumentLanguage);

// Switching language remounts the routed tree (see App.js), which re-runs every
// page's fetch effect — but a zustand store lives OUTSIDE React and survives
// that remount. `useOrgScopeStore` caches the in-scope organization list behind
// a `loaded` flag so it survives navigation between the analytics pages, and
// Organization.name is translated. Without this the school selector would keep
// serving names in the previous language until the next full reload.
//
// It is the only store that caches server-owned text; the other two hold a
// local preference and modal payloads. If a future store caches translated
// content, it needs clearing here too.
// Invalidate, not reset: only the cached NAMES went stale. A full reset also
// dropped which school the person had drilled into, so every language switch
// threw a head-office user back to the whole-network rollup.
i18n.on("languageChanged", () => {
  useOrgScopeStore.getState().invalidateOrganizations();
});

export default i18n;
